import axios from 'axios';
import { message } from 'antd';

/**
 * 机会雷达 API 服务 — 独立 axios 实例
 * 请求路径直接匹配后端 @RequestMapping("/api/opportunity-radar")
 * Vite proxy 将 /api 转发到 http://localhost:3001
 */

const request = axios.create({
  baseURL: '/api/opportunity-radar',
  timeout: 30000,
});

/** 统一从 Zustand persist 中读取 token */
const getToken = () => {
  try {
    const raw = localStorage.getItem('simon-cat-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.token || null;
  } catch {
    return null;
  }
};

request.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (response) => {
    const { data } = response;
    // ApiResult 格式：{ code: 200, message: "success", data: T }
    if (data && data.code !== undefined) {
      if (data.code === 200 || data.code === 0) {
        return data;
      }
      const errMsg = data.message || '请求失败';
      message.error(errMsg);
      return Promise.reject(new Error(errMsg));
    }
    return data;
  },
  (error) => {
    const errMsg = error?.response?.data?.message || error.message || '网络异常';
    message.error(errMsg);
    return Promise.reject(error);
  }
);

export const opportunityApi = {
  /**
   * 获取仪表盘统计数据
   */
  getDashboardStats() {
    return request({ url: '/dashboard', method: 'GET' });
  },

  /**
   * 获取机会列表
   */
  getOpportunityList(params = {}) {
    return request({ url: '/opportunities', method: 'GET', params });
  },

  /**
   * 获取机会详情
   */
  getOpportunityDetail(id) {
    return request({ url: `/opportunities/${id}`, method: 'GET' });
  },

  /**
   * 获取活跃信号
   */
  getActiveSignals(signalType) {
    return request({ url: '/signals', method: 'GET', params: signalType ? { signalType } : {} });
  },

  /**
   * 获取活跃场景
   */
  getActiveScenarios() {
    return request({ url: '/scenarios', method: 'GET' });
  },

  /**
   * 触发信号扫描
   */
  scanSignals() {
    return request({ url: '/scan', method: 'POST' });
  },

  /**
   * 触发场景识别
   */
  detectScenarios() {
    return request({ url: '/detect', method: 'POST' });
  },

  /**
   * 触发机会发现
   */
  discoverOpportunities() {
    return request({ url: '/discover', method: 'POST' });
  },

  /**
   * 更新机会反馈
   */
  updateFeedback(id, feedback) {
    return request({ url: `/opportunities/${id}/feedback`, method: 'PUT', data: { feedback } });
  },

  /**
   * 获取近7天趋势
   */
  getWeeklyTrend() {
    return request({ url: '/trend', method: 'GET' });
  },

  /**
   * 获取信号类型统计
   */
  getSignalTypeStats() {
    return request({ url: '/signal-stats', method: 'GET' });
  },
};

export default opportunityApi;
